import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/dealership/lib/api";
import { api as rootApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RepSignature from "@/components/signatures/RepSignature";
import { computeWorksheet, money } from "@/dealership/lib/desk";
import { loadDealer, mergeDealer } from "@/dealership/lib/dealer";
import { usePrintFit } from "@/dealership/lib/printFit";
import { Printer, Mail, ArrowLeft } from "lucide-react";

const Row = ({ label, value, bold, sub }) => (
  <div className={`flex items-baseline justify-between gap-4 py-1.5 ${sub ? "pl-4 text-sm" : ""} ${bold ? "border-t border-neutral-300 mt-1 pt-2 font-semibold" : ""}`}>
    <span className={sub ? "text-neutral-600" : ""}>{label}</span>
    <span className="tabular-nums">{typeof value === "number" ? money(value) : value}</span>
  </div>
);

export default function DeskQuote() {
  const { wsId } = useParams();
  const [params] = useSearchParams();
  const internal = params.get("internal") === "1";
  const [emailTo, setEmailTo] = useState("");
  const [sending, setSending] = useState(false);
  const [picked, setPicked] = useState(null);
  const [sigOverride, setSigOverride] = useState("");
  const qc = useQueryClient();

  const { data: settings } = useQuery({ queryKey: ["desk-settings"], queryFn: async () => (await api.get("/desking/settings")).data });
  const { data: ws } = useQuery({ queryKey: ["worksheet", wsId], queryFn: async () => (await api.get(`/desking/worksheets/${wsId}`)).data });
  const { data: reps = [] } = useQuery({ queryKey: ["sig-consented"], queryFn: async () => (await rootApi.get("/signatures/consented-users")).data });
  const { data: siblings = [] } = useQuery({
    queryKey: ["worksheet-siblings", ws?.lead_id, ws?.vehicle_id],
    enabled: Boolean(ws && (ws.lead_id || ws.vehicle_id)),
    queryFn: async () => {
      const p = ws.lead_id ? { lead_id: ws.lead_id } : { vehicle_id: ws.vehicle_id };
      return (await api.get("/desking/worksheets", { params: p })).data;
    },
  });

  const totals = useMemo(() => (ws && settings ? computeWorksheet(ws, settings) : null), [ws, settings]);
  const dealer = useMemo(() => mergeDealer(loadDealer(), settings?.dealer), [settings]);
  usePrintFit(totals);

  // Up to three payment options: sibling scenarios if they exist, otherwise
  // term variations of this structure.
  const options = useMemo(() => {
    if (!ws || !settings || !totals) return [];
    if (totals.mode === "cash") return [];
    const others = siblings.filter((s) => s.id !== wsId && s.totals?.mode !== "cash").slice(0, 2);
    const base = [{ id: wsId, label: ws.name || "Option 1", totals }];
    if (others.length) {
      return [...base, ...others.map((s) => ({ id: s.id, label: s.name || "Option", totals: s.totals }))];
    }
    const term = Number(ws.finance?.term_months) || 72;
    const variants = [term, Math.min(term + 12, 96), Math.max(term - 12, 24)].filter((t, i, arr) => arr.indexOf(t) === i);
    return variants.map((t) => ({
      id: `t${t}`,
      label: `${t} months`,
      totals: computeWorksheet({ ...ws, finance: { ...ws.finance, term_months: t } }, settings),
    }));
  }, [ws, settings, totals, siblings, wsId]);

  const shown = (picked ? options.filter((o) => picked.includes(o.id)) : options).slice(0, 3);

  const quoteHtml = () => {
    if (!totals) return "";
    const repSig = ws.rep_signature;
    const sigHtml = repSig ? `<div style="margin-top:22px">
        <img src="${repSig.image}" alt="signature" style="max-height:44px;display:block"/>
        <div style="border-top:1px solid #333;width:240px;padding-top:4px;font-size:11px;color:#333">
          For ${dealer.name}<br/><b>${repSig.printed_name || ""}</b>${repSig.title ? `<br/>${repSig.title}` : ""}<br/>${new Date().toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}
        </div></div>` : "";
    const rows = shown.map((o) => `<td style="padding:8px;border:1px solid #ddd;text-align:center">
        <div style="font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#666">${o.label}</div>
        <div style="font-size:22px;font-weight:700;margin:4px 0">${money(o.totals.payment)}</div>
        <div style="font-size:12px;color:#666">${o.totals.frequency_label} · ${o.totals.term_months} mo · ${o.totals.apr}% APR</div>
        <div style="font-size:11px;color:#888;margin-top:4px">Cost of borrowing ${money(o.totals.cost_of_borrowing)}</div>
      </td>`).join("");
    return `<div style="font-family:Arial,Helvetica,sans-serif;color:#111">
      <h2 style="margin:0 0 4px">${dealer.name}</h2>
      <div style="font-size:12px;color:#555">${dealer.address} · ${dealer.phone}</div>
      <h3 style="margin:18px 0 6px">Vehicle purchase quote</h3>
      <div style="font-size:13px"><b>${ws.customer_name || ""}</b> — ${ws.vehicle_desc || ""}</div>
      <table style="border-collapse:collapse;margin:14px 0;font-size:13px">
        <tr><td style="padding:4px 12px 4px 0">Selling price</td><td style="text-align:right">${money(totals.selling_price_net)}</td></tr>
        ${ws.has_trade ? `<tr><td style="padding:4px 12px 4px 0">Trade-in allowance</td><td style="text-align:right">-${money(totals.trade_allowance)}</td></tr>` : ""}
        ${totals.fees_total ? `<tr><td style="padding:4px 12px 4px 0">Fees</td><td style="text-align:right">${money(totals.fees_total)}</td></tr>` : ""}
        ${totals.products_total ? `<tr><td style="padding:4px 12px 4px 0">Protection products</td><td style="text-align:right">${money(totals.products_total)}</td></tr>` : ""}
        ${totals.luxury_tax ? `<tr><td style="padding:4px 12px 4px 0">Federal luxury tax</td><td style="text-align:right">${money(totals.luxury_tax)}</td></tr>` : ""}
        <tr><td style="padding:4px 12px 4px 0">HST ${totals.tax_exempt ? "(exempt)" : `${totals.tax_rate}%`}</td><td style="text-align:right">${totals.tax_exempt ? "No tax" : money(totals.tax)}</td></tr>
        <tr><td style="padding:6px 12px 4px 0;font-weight:700;border-top:1px solid #ccc">${totals.mode === "cash" ? "Balance due" : "Amount financed"}</td><td style="text-align:right;font-weight:700;border-top:1px solid #ccc">${money(totals.mode === "cash" ? totals.cash_due : totals.amount_financed)}</td></tr>
      </table>
      ${shown.length ? `<table style="border-collapse:collapse"><tr>${rows}</tr></table>` : ""}
      <p style="font-size:11px;color:#666;margin-top:16px">${settings?.quote_disclaimer || ""}</p>
      <p style="font-size:12px">${ws.salesperson ? `Your salesperson: ${ws.salesperson}` : ""}${ws.valid_until ? ` · Valid until ${ws.valid_until}` : ""}</p>
      ${sigHtml}
    </div>`;
  };

  const sendEmail = async () => {
    const to = emailTo || ws?.customer_email;
    if (!to) return toast.error("Enter an email address");
    setSending(true);
    try {
      await api.post(`/desking/worksheets/${wsId}/email-quote`, { to, html: quoteHtml() });
      toast.success(`Quote emailed to ${to}`);
      rootApi.post("/signatures/applied", { doc_type: "desking_quote", doc_id: wsId, override_user_id: sigOverride }).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send — use Print / PDF or the mail app button.");
    } finally { setSending(false); }
  };

  const mailto = () => {
    const to = emailTo || ws?.customer_email || "";
    const body = `Hi ${ws?.customer_name || ""},\n\nHere is your quote on the ${ws?.vehicle_desc || "vehicle"}:\n\n` +
      shown.map((o) => `${o.label}: ${money(o.totals.payment)} ${o.totals.frequency_label.toLowerCase()} · ${o.totals.term_months} months @ ${o.totals.apr}% APR`).join("\n") +
      `\n\n${totals?.mode === "cash" ? "Balance due" : "Amount financed"}: ${money(totals?.mode === "cash" ? totals?.cash_due : totals?.amount_financed)}\n\n` +
      `${settings?.quote_disclaimer || ""}\n\n${ws?.salesperson || ""}\n${dealer.name}\n${dealer.phone}`;
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(`Your quote from ${dealer.name}`)}&body=${encodeURIComponent(body)}`;
  };

  if (!ws || !totals) return <div className="p-8 text-muted-foreground">Loading quote…</div>;

  const applyOverride = async (uid) => {
    const v = uid === "auto" ? "" : uid;
    setSigOverride(v);
    const resp = await api.get(`/desking/worksheets/${wsId}`, { params: v ? { sig_override: v } : {} });
    qc.setQueryData(["worksheet", wsId], (old) => (old ? { ...old, rep_signature: resp.data.rep_signature } : old));
  };
  const handlePrint = () => {
    rootApi.post("/signatures/applied", { doc_type: "desking_quote", doc_id: wsId, override_user_id: sigOverride }).catch(() => {});
    window.print();
  };

  return (
    <div className="print-sheet min-h-screen bg-neutral-100 py-6">

      <div className="no-print max-w-[8.5in] mx-auto mb-4 flex items-center gap-2 flex-wrap px-4">
        <Button size="sm" variant="outline" onClick={() => window.close()} className="bg-white text-neutral-900 border-neutral-300 hover:bg-neutral-100 hover:text-neutral-900" data-testid="quote-close"><ArrowLeft className="h-3.5 w-3.5 mr-1.5" />Close</Button>
        <Button size="sm" onClick={handlePrint} data-testid="quote-print"><Printer className="h-3.5 w-3.5 mr-1.5" />Print / PDF</Button>
        {!internal && reps.length > 0 && (
          <Select value={sigOverride || "auto"} onValueChange={applyOverride}>
            <SelectTrigger className="h-9 w-52 bg-white text-neutral-900 border-neutral-300" data-testid="quote-sig-override"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Signed by: Me (auto)</SelectItem>
              {reps.map((r) => <SelectItem key={r.user_id} value={r.user_id}>Signed by {r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {!internal && (
          <>
            <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder={ws.customer_email || "customer@email.com"} className="h-9 w-56 bg-white text-neutral-900" data-testid="quote-email-input" />
            <Button size="sm" variant="outline" onClick={sendEmail} disabled={sending} className="bg-white text-neutral-900 border-neutral-300 hover:bg-neutral-100 hover:text-neutral-900" data-testid="quote-email-send"><Mail className="h-3.5 w-3.5 mr-1.5" />{sending ? "Sending…" : "Send via Gmail"}</Button>
            <Button size="sm" variant="ghost" onClick={mailto} className="text-neutral-700 hover:bg-neutral-200 hover:text-neutral-900" data-testid="quote-mailto">Open in mail app</Button>
          </>
        )}
        {options.length > 1 && !internal && (
          <div className="flex items-center gap-1.5 ml-auto">
            {options.map((o) => {
              const on = shown.some((s) => s.id === o.id);
              return (
                <button key={o.id} data-testid={`quote-option-toggle-${o.id}`}
                  onClick={() => {
                    const cur = picked || options.map((x) => x.id);
                    setPicked(on ? cur.filter((x) => x !== o.id) : [...cur, o.id]);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs border ${on ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-600 border-neutral-300"}`}>
                  {o.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="print-area mx-auto bg-white text-neutral-900 p-8 shadow-lg" data-testid={internal ? "internal-worksheet-print" : "customer-quote-sheet"}>
        {internal && (
          <div className="mb-4 border-2 border-red-600 text-red-700 text-center text-xs font-bold uppercase tracking-widest py-1.5">
            Internal worksheet — not for the customer
          </div>
        )}
        <div className="flex items-start justify-between gap-6 border-b border-neutral-300 pb-4">
          <div>
            <div className="text-xl font-bold">{dealer.name}</div>
            <div className="text-xs text-neutral-600 mt-1">{dealer.address}</div>
            <div className="text-xs text-neutral-600">{dealer.phone}{dealer.email ? ` · ${dealer.email}` : ""}</div>
            <div className="text-xs text-neutral-600">OMVIC / Reg #{dealer.reg} · HST {dealer.hst_number}</div>
          </div>
          <div className="text-right">
            <div className="text-lg font-semibold">{internal ? "Desk Worksheet" : "Vehicle Purchase Quote"}</div>
            <div className="text-xs text-neutral-600 mt-1">{new Date().toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}</div>
            {ws.valid_until && <div className="text-xs text-neutral-600">Valid until {ws.valid_until}</div>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 mt-5 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Prepared for</div>
            <div className="font-semibold">{ws.customer_name || "—"}</div>
            {ws.customer_phone && <div className="text-neutral-600">{ws.customer_phone}</div>}
            {ws.customer_email && <div className="text-neutral-600">{ws.customer_email}</div>}
            {ws.customer_address && <div className="text-neutral-600">{ws.customer_address}</div>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Vehicle</div>
            <div className="font-semibold">{ws.vehicle_desc || "—"}</div>
            {ws.vehicle_vin && <div className="text-neutral-600 font-mono text-xs">VIN {ws.vehicle_vin}</div>}
            {ws.vehicle_stock && <div className="text-neutral-600 text-xs">Stock #{ws.vehicle_stock}</div>}
            {ws.vehicle_odometer ? <div className="text-neutral-600 text-xs tabular-nums">{Number(ws.vehicle_odometer).toLocaleString()} km</div> : null}
          </div>
        </div>

        <div className="mt-6 text-sm">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Purchase breakdown</div>
          <Row label="Selling price" value={totals.price_gross} />
          {totals.discount > 0 && <Row label="Discount" value={-totals.discount} sub />}
          {totals.rebate_before_tax > 0 && <Row label="Rebate (before tax)" value={-totals.rebate_before_tax} sub />}
          {ws.has_trade && (
            <>
              <Row label={`Trade-in allowance${ws.trade?.desc ? ` — ${ws.trade.desc}` : ""}`} value={-totals.trade_allowance} />
              {totals.trade_lien > 0 && <Row label={`Lien payout${ws.trade?.lienholder ? ` to ${ws.trade.lienholder}` : ""}`} value={totals.trade_lien} sub />}
            </>
          )}
          {(ws.fees || []).filter((f) => f.enabled !== false && f.amount).map((f) => (
            <Row key={f.id} label={`${f.label}${f.taxable === false ? " (no tax)" : ""}`} value={f.amount} sub />
          ))}
          {(ws.products || []).filter((p) => p.enabled !== false && p.price).map((p) => (
            <Row key={p.id} label={`${p.name}${p.term_months ? ` — ${p.term_months} mo${p.term_km ? ` / ${Number(p.term_km).toLocaleString()} km` : ""}` : ""}${p.financed === false ? " (paid upfront)" : ""}`} value={p.price} sub />
          ))}
          {totals.luxury_tax > 0 && <Row label="Federal luxury tax" value={totals.luxury_tax} sub />}
          <Row label={totals.tax_exempt ? `HST — exempt${ws.tax_note ? ` (${ws.tax_note})` : ""}` : `HST ${totals.tax_rate}%`} value={totals.tax_exempt ? "No tax" : totals.tax} />
          {totals.rebate_after_tax > 0 && <Row label="Rebate / credit" value={-totals.rebate_after_tax} sub />}
          {totals.deposit > 0 && <Row label="Deposit received" value={-totals.deposit} sub />}
          {totals.cash_down > 0 && <Row label="Cash down" value={-totals.cash_down} sub />}
          <Row label={totals.mode === "cash" ? "Balance due on delivery" : "Amount financed"} value={totals.mode === "cash" ? totals.cash_due : totals.amount_financed} bold />
          {totals.tax_savings_from_trade > 0 && (
            <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
              Trading in saves you {money(totals.tax_savings_from_trade)} in HST on this purchase.
            </div>
          )}
        </div>

        {shown.length > 0 && (
          <div className="mt-7">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Payment options</div>
            <div className={`grid gap-3 ${shown.length === 3 ? "grid-cols-3" : shown.length === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
              {shown.map((o) => (
                <div key={o.id} className="border border-neutral-300 rounded p-4 text-center" data-testid={`quote-option-${o.id}`}>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">{o.label}</div>
                  <div className="text-2xl font-bold mt-1 tabular-nums">{money(o.totals.payment)}</div>
                  <div className="text-xs text-neutral-600">{o.totals.frequency_label}</div>
                  <div className="mt-3 text-xs text-neutral-700 space-y-0.5">
                    <div>{o.totals.term_months} months @ {o.totals.apr}% APR</div>
                    <div>{o.totals.n_payments} payments</div>
                    <div>Amount financed {money(o.totals.amount_financed)}</div>
                    <div>Total of payments {money(o.totals.total_of_payments)}</div>
                    <div>Cost of borrowing {money(o.totals.cost_of_borrowing)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!internal && (
          <div className="mt-8" data-testid="quote-signature-block">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Acceptance</div>
            <p className="text-[11px] text-neutral-600 mb-6">
              Signing below confirms the structure above and authorizes {dealer.name} to submit it for credit approval. It is not a credit approval on its own.
            </p>
            <div className="grid grid-cols-2 gap-10 text-xs">
              <div>
                <div className="border-b border-neutral-500 h-8" />
                <div className="text-neutral-500 mt-1">Purchaser — {ws.customer_name || "signature"}</div>
                <div className="border-b border-neutral-500 h-8 mt-6 w-40" />
                <div className="text-neutral-500 mt-1">Date</div>
              </div>
              <div data-testid="quote-rep-signature">
                {ws.rep_signature ? (
                  <RepSignature
                    signature={ws.rep_signature}
                    label={`For ${dealer.name}${ws.salesperson ? ` — ${ws.salesperson}` : ""}`}
                    date={new Date().toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}
                  />
                ) : (
                  <>
                    <div className="border-b border-neutral-500 h-8" />
                    <div className="text-neutral-500 mt-1">For {dealer.name}{ws.salesperson ? ` — ${ws.salesperson}` : ""}</div>
                    <div className="border-b border-neutral-500 h-8 mt-6 w-40" />
                    <div className="text-neutral-500 mt-1">Date</div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {internal && (
          <div className="mt-7 border-2 border-red-300 rounded p-4" data-testid="internal-gross-block">
            <div className="text-[10px] uppercase tracking-widest text-red-700 mb-2">Internal — do not present</div>
            <div className="grid grid-cols-2 gap-6 text-sm">
              <div>
                <Row label="Landed cost" value={totals.cost_basis} />
                <Row label="Pack" value={totals.pack} />
                {ws.has_trade && <Row label="Trade ACV" value={totals.trade_acv} />}
                {totals.over_allowance > 0 && <Row label="Over-allowance" value={totals.over_allowance} />}
                <Row label="Front-end gross" value={totals.front_gross} bold />
              </div>
              <div>
                <Row label="Product retail" value={totals.products_total} />
                <Row label="Product cost" value={totals.products_cost} />
                {totals.finance_reserve > 0 && <Row label="Finance reserve" value={totals.finance_reserve} />}
                <Row label="Back-end gross" value={totals.back_gross} bold />
                <Row label="Total deal gross" value={totals.total_gross} bold />
              </div>
            </div>
            {ws.notes && <div className="mt-3 text-xs text-neutral-700 whitespace-pre-wrap">Notes: {ws.notes}</div>}
          </div>
        )}

        <div className="mt-7 pt-4 border-t border-neutral-300 text-[11px] text-neutral-600 leading-relaxed">
          {settings?.quote_disclaimer}
          {totals.mode !== "cash" && (
            <div className="mt-2">
              Cost of borrowing disclosure: on an amount financed of {money(totals.amount_financed)} at {totals.apr}% APR over {totals.term_months} months
              ({totals.n_payments} {totals.frequency_label.toLowerCase()} payments of {money(totals.payment)}), the total of payments is {money(totals.total_of_payments)}
              and the total cost of borrowing is {money(totals.cost_of_borrowing)}.
              {totals.deferral_days > 0 && ` First payment deferred ${totals.deferral_days} days (${money(totals.deferral_interest)} interest added).`}
            </div>
          )}
        </div>

        <div className="mt-5 flex items-end justify-between text-xs">
          <div>
            <div className="text-neutral-500">Salesperson</div>
            <div className="font-semibold">{ws.salesperson || "—"}</div>
          </div>
          <div className="text-right text-neutral-500">Quote #{String(wsId).slice(0, 8).toUpperCase()}</div>
        </div>
      </div>
    </div>
  );
}
